import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { useI18n } from '../lib/i18n';
import { formatDate, formatGel, formatPoints } from '../lib/format';
import { useCreateCustomer, useCustomers } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import { can, useSession } from '../lib/session';
import {
  Alert, Amount, Button, Card, EmptyState, Field, Pill, SectionTitle, Skeleton, TierBadge,
} from '../components/ui';

function CreateCustomerForm({ onDone }: { onDone: (id: string) => void }) {
  const { t } = useI18n();
  const create = useCreateCustomer();
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', referredByCode: '',
    make: '', model: '', licensePlate: '',
  });

  const errorText = useApiError();
  const error = create.error instanceof ApiError ? create.error : null;
  const duplicateId = error?.code === 'DUPLICATE_PHONE'
    ? String(error.details['customerId'] ?? '')
    : null;

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <Card>
      <SectionTitle>{t('customers.new')}</SectionTitle>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({
            firstName: form.firstName,
            ...(form.lastName ? { lastName: form.lastName } : {}),
            phone: form.phone,
            ...(form.email ? { email: form.email } : {}),
            ...(form.referredByCode ? { referredByCode: form.referredByCode } : {}),
            ...(form.make && form.model
              ? { vehicle: { make: form.make, model: form.model, licensePlate: form.licensePlate || undefined } }
              : {}),
          }, { onSuccess: (created) => onDone(created.id) });
        }}
      >
        {error && (
          <div className="sm:col-span-2">
            <Alert>
              {errorText(error) ?? error.message}
              {duplicateId && (
                <Link to={`/customers/${duplicateId}`} className="ml-2 font-semibold text-brand underline">
                  {t('customers.openThem', { name: String(error.details['name'] ?? '') })}
                </Link>
              )}
            </Alert>
          </div>
        )}

        <Field label={t('common.firstName')} required value={form.firstName} onChange={set('firstName')} />
        <Field label={t('common.lastName')} value={form.lastName} onChange={set('lastName')} />
        <Field
          label={t('common.phone')} required inputMode="tel" placeholder="555 00 12 34"
          value={form.phone} onChange={set('phone')}
        />
        <Field label={t('common.email')} type="email" value={form.email} onChange={set('email')} />
        <Field
          label={t('customers.referredBy')}
          placeholder={t('customers.referralCodePlaceholder')}
          value={form.referredByCode}
          onChange={set('referredByCode')}
          className="uppercase"
        />

        <div className="sm:col-span-2 border-t border-line pt-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-faint">
            {t('customers.firstVehicle')}
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t('customers.make')} placeholder="BMW" value={form.make} onChange={set('make')} />
            <Field label={t('customers.model')} placeholder="M5" value={form.model} onChange={set('model')} />
            <Field label={t('customers.plate')} placeholder="XX-123-XX" value={form.licensePlate} onChange={set('licensePlate')} />
          </div>
        </div>

        <div className="sm:col-span-2">
          <Button type="submit" loading={create.isPending}>{t('customers.create')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function CustomersPage() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const debounced = useDebounced(search);
  const { data, isPending, isFetching } = useCustomers(debounced);

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
            {t('customers.title')}
          </h1>
        </div>
        {can(session, 'customers.create') && (
          <Button variant={creating ? 'ghost' : 'primary'} onClick={() => setCreating((v) => !v)}>
            {creating ? t('common.cancel') : t('customers.new')}
          </Button>
        )}
      </header>

      {creating && (
        <div className="mb-6">
          <CreateCustomerForm onDone={(id) => navigate(`/customers/${id}`)} />
        </div>
      )}

      <div className="relative mb-4">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('customers.searchPlaceholder')}
          aria-label={t('customers.search')}
          className="w-full rounded-xl border border-line bg-raised px-4 py-3 text-ink transition-colors placeholder:text-ink-faint focus:border-brand focus:bg-muted focus:outline-none"
        />
        {isFetching && !isPending && (
          <span className="absolute right-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        )}
      </div>

      {isPending && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[4.5rem]" />)}
        </div>
      )}

      {!isPending && items.length === 0 && (
        <EmptyState
          title={debounced ? t('customers.noMatch', { query: debounced }) : t('customers.empty')}
          hint={debounced ? t('customers.searchTry') : undefined}
          {...(can(session, 'customers.create') && !creating
            ? { action: <Button size="sm" onClick={() => setCreating(true)}>{t('customers.new')}</Button> }
            : {})}
        />
      )}

      <div className="space-y-2">
        {items.map((customer) => (
          <Link
            key={customer.id}
            to={`/customers/${customer.id}`}
            className="group block rounded-2xl border border-line bg-surface px-4 py-3.5 transition-colors hover:border-brand/40 hover:bg-raised sm:px-5 sm:py-4"
          >
            <div className="sm:flex sm:items-center sm:gap-6">
              <div className="min-w-0 sm:flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink transition-colors group-hover:text-brand">
                    {customer.name}
                  </span>
                  {customer.tierCode && <TierBadge code={customer.tierCode} size="sm" />}
                  {customer.status === 'BLOCKED' && <Pill tone="danger">{t('customer.blockedPill')}</Pill>}
                </div>
                <div className="tabular mt-1 truncate text-xs text-ink-faint">
                  {customer.phone}
                  <span className="mx-1.5 opacity-50">·</span>
                  {customer.loyaltyId}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-4 border-t border-line/60 pt-2.5 sm:mt-0 sm:gap-6 sm:border-0 sm:pt-0">
                <div className="sm:text-right">
                  <Amount
                    value={formatGel(customer.lifetimeSpendTetri ?? 0)}
                    className="block text-sm font-semibold text-ink"
                  />
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('customers.lifetime')}</div>
                </div>
                <div className="sm:text-right">
                  <Amount
                    value={formatGel(customer.cashbackAvailableTetri ?? 0)}
                    className="block text-sm font-semibold text-brand"
                    symbolClassName="text-brand/60"
                  />
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('common.cashback')}</div>
                </div>
                <div className="tabular sm:text-right">
                  <div className="text-sm font-semibold text-ink">
                    {formatPoints(customer.pointsBalance ?? 0)}
                  </div>
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('common.points')}</div>
                </div>
                <div className="ml-auto hidden w-24 text-right text-xs text-ink-faint lg:block">
                  {customer.lastVisitAt ? formatDate(customer.lastVisitAt) : t('customer.neverVisited')}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
